import * as React from 'react';
import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Alert,
  Slider,
  FormHelperText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  RadioGroup,
  FormControlLabel,
  Radio,
  Collapse,
} from '@mui/material';
import {
  Person as PersonIcon,
  Psychology as PersonalityIcon,
  History as HistoryIcon,
  PowerSettingsNew as PowerIcon,
  People as RelationshipIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Refresh as RefineIcon,
  ExpandMore as ExpandIcon,
  Timeline as TimelineIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { CharacterService } from '../services/characterService';
import { CharacterSession, CharacterDetails } from '../models/character';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

export const CharacterAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mainTabValue, setMainTabValue] = useState(0);
  const [characterTabValue, setCharacterTabValue] = useState(0);
  const [sessions, setSessions] = useState<CharacterSession[]>([]);
  const [currentSession, setCurrentSession] = useState<CharacterSession | null>(null);
  const [characterConcept, setCharacterConcept] = useState('');
  const [genre, setGenre] = useState('fantasy');
  const [style, setStyle] = useState('detailed');
  const [purpose, setPurpose] = useState('novel');
  const [feedback, setFeedback] = useState('');
  const [aspectToRefine, setAspectToRefine] = useState<string>('all');
  const [agentType, setAgentType] = useState<'Architect' | 'Alchemist'>('Architect');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupExpanded, setSetupExpanded] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (currentSession && setupExpanded) {
      setSetupExpanded(false);
    }
  }, [currentSession]);

  const loadSessions = () => {
    const allSessions = CharacterService.getAllSessions();
    setSessions(allSessions);
  };

  const handleStartSession = async () => {
    if (!characterConcept.trim()) {
      setError('Please enter a character concept');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sessionName = `Character-${Date.now()}`;
      const response = await CharacterService.startSession({
        SessionName: sessionName,
        InitialPrompt: characterConcept,
        genre,
        style,
        purpose,
        temperature,
        maxTokens,
      });

      console.log('Character creation response:', response);

      const session = CharacterService.createLocalSession(
        response.sessionId,
        characterConcept,
        response.character,
        sessionName
      );

      setCurrentSession(session);
      loadSessions();
      setCharacterConcept('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start character session');
    } finally {
      setLoading(false);
    }
  };

  const handleIterate = async () => {
    if (!currentSession || !feedback.trim()) {
      setError('Please provide feedback for refinement');
      return;
    }

    setLoading(true);
    setError(null);

    // Map our aspectToRefine to the API's DetailDomain
    const detailDomainMap: Record<string, string> = {
      'all': 'Personality', // Default to Personality for 'all'
      'appearance': 'Physique',
      'personality': 'Personality',
      'backstory': 'Background',
      'facial': 'Facial',
      'clothing': 'Clothing',
      'abilities': 'Physique',
      'relationships': 'Background'
    };

    try {
      const response = await CharacterService.iterate(currentSession.sessionId, {
        AgentType: agentType,
        DetailDomain: detailDomainMap[aspectToRefine] || 'Personality',
        feedback,
        temperature,
        maxResponseTokens: maxTokens,
      });

      console.log('Character iteration response:', response);

      CharacterService.addHistory(
        currentSession.id,
        response.character,
        feedback,
        response.changes
      );

      setCurrentSession(CharacterService.getSession(currentSession.id));
      setFeedback('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to refine character');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = (session: CharacterSession) => {
    setCurrentSession(session);
    setMainTabValue(0); // Switch to Create tab
  };

  const handleDeleteSession = (id: string) => {
    CharacterService.deleteSession(id);
    if (currentSession?.id === id) {
      setCurrentSession(null);
    }
    loadSessions();
  };

  const handleExport = (format: 'markdown' | 'json') => {
    if (!currentSession) return;

    const content = CharacterService.exportCharacter(currentSession, format);
    const blob = new Blob([content], { type: format === 'json' ? 'application/json' : 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `character-${currentSession.currentCharacter.name.toLowerCase().replace(/\s+/g, '-')}.${format === 'json' ? 'json' : 'md'}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderCharacterDetails = (character: CharacterDetails) => {
    if (!character) {
      return (
        <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
          <Typography 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            No character data available
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            flexWrap: 'wrap'
          }}
        >
          <Avatar sx={{ 
            bgcolor: 'primary.main',
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 }
          }}>
            <PersonIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
          </Avatar>
          {character.name || 'Unnamed Character'}
        </Typography>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Basic Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  <PersonIcon sx={{ fontSize: { xs: 18, sm: 20 } }} /> {isMobile ? 'Info' : 'Basic Information'}
                </Typography>
                {character.age && (
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <strong>Age:</strong> {character.age}
                  </Typography>
                )}
                {character.gender && (
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <strong>Gender:</strong> {character.gender}
                  </Typography>
                )}
                {character.species && (
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <strong>Species:</strong> {character.species}
                  </Typography>
                )}
                {character.occupation && (
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <strong>Occupation:</strong> {character.occupation}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Appearance */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                >
                  Appearance
                </Typography>
                <Typography 
                  paragraph
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {character.appearance?.physicalDescription || 'No description available'}
                </Typography>
                {character.appearance?.clothingStyle && (
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <strong>Clothing:</strong> {character.appearance.clothingStyle}
                  </Typography>
                )}
                {character.appearance?.distinguishingFeatures && character.appearance.distinguishingFeatures.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography 
                      variant="subtitle2"
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      Distinguishing Features:
                    </Typography>
                    {character.appearance.distinguishingFeatures.map((f, i) => (
                      <Chip 
                        key={i} 
                        label={f} 
                        size="small" 
                        sx={{ 
                          mr: 0.5, 
                          mb: 0.5,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }} 
                      />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Personality */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  <PersonalityIcon sx={{ fontSize: { xs: 18, sm: 20 } }} /> Personality
                </Typography>
                <Typography 
                  paragraph
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <strong>Temperament:</strong> {character.personality?.temperament || 'Unknown'}
                </Typography>
                
                {character.personality?.traits && character.personality.traits.length > 0 && (
                  <>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      Traits:
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      {character.personality.traits.map((t, i) => (
                        <Chip 
                          key={i} 
                          label={t} 
                          color="primary" 
                          variant="outlined" 
                          sx={{ 
                            mr: 0.5, 
                            mb: 0.5,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                          }} 
                        />
                      ))}
                    </Box>
                  </>
                )}

                {character.personality?.fears && character.personality.fears.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>Fears:</Typography>
                    <Box sx={{ mb: 2 }}>
                      {character.personality.fears.map((f, i) => (
                        <Chip key={i} label={f} color="error" variant="outlined" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  </>
                )}

                {character.personality?.desires && character.personality.desires.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>Desires:</Typography>
                    <Box sx={{ mb: 2 }}>
                      {character.personality.desires.map((d, i) => (
                        <Chip key={i} label={d} color="success" variant="outlined" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Backstory */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon /> Backstory
                </Typography>
                <Typography paragraph>{character.backstory?.origin || 'No backstory available'}</Typography>
                
                {character.backstory?.keyEvents && character.backstory.keyEvents.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandIcon />}>
                      <Typography>Key Events</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List>
                        {character.backstory.keyEvents.map((e, i) => (
                          <ListItem key={i}>
                            <ListItemText primary={e} />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Abilities */}
          {character.abilities && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PowerIcon /> Abilities
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {character.abilities.skills && character.abilities.skills.length > 0 && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>Skills:</Typography>
                        {character.abilities.skills.map((s, i) => (
                          <Typography key={i} variant="body2">• {s}</Typography>
                        ))}
                      </Grid>
                    )}
                    
                    {character.abilities.powers && character.abilities.powers.length > 0 && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>Powers:</Typography>
                        {character.abilities.powers.map((p, i) => (
                          <Typography key={i} variant="body2">• {p}</Typography>
                        ))}
                      </Grid>
                    )}
                    
                    {character.abilities.weaknesses && character.abilities.weaknesses.length > 0 && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>Weaknesses:</Typography>
                        {character.abilities.weaknesses.map((w, i) => (
                          <Typography key={i} variant="body2">• {w}</Typography>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Relationships */}
          {character.relationships && character.relationships.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RelationshipIcon /> Relationships
                  </Typography>
                  {character.relationships.map((r, i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">
                        {r.characterName} ({r.relationshipType})
                      </Typography>
                      <Typography variant="body2">{r.description}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 1, sm: 2 }, 
            mb: 2,
            flexWrap: 'wrap'
          }}>
            <PersonIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
            <Typography 
              variant="h4" 
              component="h1"
              sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}
            >
              {isMobile ? 'Character Agent' : 'Character Creation Agent'}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Paper 
          elevation={0}
          sx={{ 
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Tabs 
              value={mainTabValue} 
              onChange={(_, newValue) => setMainTabValue(newValue)}
              sx={{ 
                mb: 2,
                '& .MuiTab-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: 48
                }
              }}
              variant={isMobile ? 'fullWidth' : 'standard'}
            >
              <Tab label="Create" />
              <Tab label="History" />
            </Tabs>

            <TabPanel value={mainTabValue} index={0}>
              <Paper sx={{ mb: { xs: 2, sm: 3 }, backgroundColor: 'background.default' }}>
          <Box 
            sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
            onClick={() => setSetupExpanded(!setupExpanded)}
          >
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
            >
              {isMobile ? 'Setup' : 'Character Setup'}
            </Typography>
            <IconButton size="small" sx={{ minWidth: 44, minHeight: 44 }}>
              {setupExpanded ? <ExpandLessIcon /> : <ExpandIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={setupExpanded}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>

              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} md={6}>
                  {!currentSession ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={isMobile ? 3 : 4}
                        label={isMobile ? 'Concept' : 'Character Concept'}
                        value={characterConcept}
                        onChange={(e) => setCharacterConcept(e.target.value)}
                        placeholder={isMobile ? 'Describe character...' : 'Describe your character concept...'}
                        sx={{ mb: 2 }}
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        InputLabelProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                      />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              Genre
                            </InputLabel>
                            <Select 
                              value={genre} 
                              onChange={(e) => setGenre(e.target.value)} 
                              label="Genre"
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              <MenuItem value="fantasy" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Fantasy</MenuItem>
                              <MenuItem value="scifi" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Sci-Fi</MenuItem>
                              <MenuItem value="horror" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Horror</MenuItem>
                              <MenuItem value="mystery" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Mystery</MenuItem>
                              <MenuItem value="romance" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Romance</MenuItem>
                              <MenuItem value="contemporary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Contemporary</MenuItem>
                              <MenuItem value="historical" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Historical</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              Style
                            </InputLabel>
                            <Select 
                              value={style} 
                              onChange={(e) => setStyle(e.target.value)} 
                              label="Style"
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              <MenuItem value="detailed" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Detailed</MenuItem>
                              <MenuItem value="concise" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Concise</MenuItem>
                              <MenuItem value="literary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Literary</MenuItem>
                              <MenuItem value="cinematic" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Cinematic</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                        <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Purpose
                        </InputLabel>
                        <Select 
                          value={purpose} 
                          onChange={(e) => setPurpose(e.target.value)} 
                          label="Purpose"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          <MenuItem value="novel" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Novel</MenuItem>
                          <MenuItem value="game" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Game</MenuItem>
                          <MenuItem value="screenplay" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Screenplay</MenuItem>
                          <MenuItem value="comic" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Comic</MenuItem>
                          <MenuItem value="tabletop" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Tabletop RPG</MenuItem>
                        </Select>
                      </FormControl>

                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleStartSession}
                        disabled={!characterConcept.trim() || loading}
                        startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayIcon />)}
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          minHeight: 44
                        }}
                      >
                        {loading 
                          ? (isMobile ? 'Creating...' : 'Creating Character...') 
                          : (isMobile ? 'Create' : 'Create Character')
                        }
                      </Button>
                    </>
                  ) : (
                    <>
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mb: 2,
                          fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                        }}
                      >
                        {isMobile ? `ID: ${currentSession.id.substring(0, 8)}...` : `Session: ${currentSession.id}`}
                      </Alert>

                      <TextField
                        fullWidth
                        multiline
                        rows={isMobile ? 2 : 3}
                        label={isMobile ? 'Feedback' : 'Refinement Feedback'}
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder={isMobile ? 'What to change...' : 'What would you like to change or refine?'}
                        sx={{ mb: 2 }}
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        InputLabelProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                      />

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {isMobile ? 'Agent' : 'Agent Type'}
                        </InputLabel>
                        <Select 
                          value={agentType} 
                          onChange={(e) => setAgentType(e.target.value as 'Architect' | 'Alchemist')}
                          label={isMobile ? 'Agent' : 'Agent Type'}
                          size="small"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          <MenuItem value="Architect" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {isMobile ? 'Architect' : 'Architect - Structural character design'}
                          </MenuItem>
                          <MenuItem value="Alchemist" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {isMobile ? 'Alchemist' : 'Alchemist - Transformative character elements'}
                          </MenuItem>
                        </Select>
                        <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
                          {isMobile ? 'Refinement approach' : 'Choose the type of agent for refinement approach'}
                        </FormHelperText>
                      </FormControl>

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <Typography 
                          gutterBottom
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {isMobile ? 'Refine' : 'Aspect to Refine'}
                        </Typography>
                        <RadioGroup value={aspectToRefine} onChange={(e) => setAspectToRefine(e.target.value)}>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <FormControlLabel 
                                value="appearance" 
                                control={<Radio size="small" />} 
                                label={<Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Physique</Typography>}
                              />
                              <FormControlLabel 
                                value="personality" 
                                control={<Radio size="small" />} 
                                label={<Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Personality</Typography>}
                              />
                              <FormControlLabel 
                                value="backstory" 
                                control={<Radio size="small" />} 
                                label={<Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Background</Typography>}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControlLabel 
                                value="facial" 
                                control={<Radio size="small" />} 
                                label={<Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Facial</Typography>}
                              />
                              <FormControlLabel 
                                value="clothing" 
                                control={<Radio size="small" />} 
                                label={<Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Clothing</Typography>}
                              />
                              <FormControlLabel 
                                value="all" 
                                control={<Radio size="small" />} 
                                label={<Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>General</Typography>}
                              />
                            </Grid>
                          </Grid>
                        </RadioGroup>
                      </FormControl>

                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={handleIterate}
                            disabled={!feedback.trim() || loading}
                            startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <RefineIcon />)}
                            sx={{ 
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              minHeight: 44
                            }}
                          >
                            {loading ? 'Refining...' : 'Refine'}
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => setCurrentSession(null)}
                            sx={{ 
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              minHeight: 44
                            }}
                          >
                            {isMobile ? 'New' : 'New Character'}
                          </Button>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
                
                <Grid item xs={12} md={6}>

                  <Divider sx={{ my: 2 }} />

                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    {isMobile ? 'Settings' : 'Model Settings'}
                  </Typography>

                  <FormControl fullWidth sx={{ mb: 1 }}>
                    <Typography 
                      variant="body2" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {isMobile ? 'Temp: ' : 'Temperature: '}{temperature}
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, val) => setTemperature(val as number)}
                      min={0}
                      max={1}
                      step={0.1}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{
                        '& .MuiSlider-markLabel': {
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }
                      }}
                    />
                    <FormHelperText sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      {isMobile ? '0 = focused, 1 = creative' : 'Controls creativity: 0 = focused, 1 = creative'}
                    </FormHelperText>
                  </FormControl>

                  <FormControl fullWidth>
                    <Typography 
                      variant="body2" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {isMobile ? 'Tokens: ' : 'Max Tokens: '}{maxTokens}
                    </Typography>
                    <Slider
                      value={maxTokens}
                      onChange={(_, val) => setMaxTokens(val as number)}
                      min={512}
                      max={4096}
                      step={256}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{
                        '& .MuiSlider-markLabel': {
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
              </Paper>

              <Paper sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'background.default' }}>
                {currentSession ? (
                  <>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'stretch', sm: 'center' }, 
                      mb: 2,
                      gap: { xs: 2, sm: 0 }
                    }}>
                      <Tabs 
                        value={characterTabValue} 
                        onChange={(_, val) => setCharacterTabValue(val)}
                        sx={{
                          '& .MuiTab-root': {
                            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                            minHeight: 48
                          }
                        }}
                        variant={isMobile ? 'fullWidth' : 'standard'}
                      >
                        <Tab label={isMobile ? 'Current' : 'Current Character'} />
                        <Tab label={isMobile ? 'History' : 'Development History'} />
                      </Tabs>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                        <IconButton 
                          onClick={() => handleExport('markdown')} 
                          title="Export as Markdown" 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <DownloadIcon />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleExport('json')} 
                          title="Export as JSON" 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <SaveIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    <TabPanel value={characterTabValue} index={0}>
                      {renderCharacterDetails(currentSession.currentCharacter)}
                    </TabPanel>

                    <TabPanel value={characterTabValue} index={1}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        Development History
                      </Typography>
                      {currentSession.history.map((h, index) => (
                        <Accordion 
                          key={index} 
                          defaultExpanded={index === currentSession.history.length - 1}
                          sx={{ mb: { xs: 1, sm: 2 } }}
                        >
                          <AccordionSummary 
                            expandIcon={<ExpandIcon />}
                            sx={{
                              '& .MuiAccordionSummary-content': {
                                my: { xs: 1, sm: 1.5 }
                              }
                            }}
                          >
                            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {index === 0 ? 'Initial Creation' : `Iteration ${index}`} - {new Date(h.timestamp).toLocaleString(undefined, isMobile ? {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'} : undefined)}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: { xs: 2, sm: 2 } }}>
                            {index === 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  gutterBottom
                                  sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                                >
                                  {isMobile ? 'Concept:' : 'Original Concept:'}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontStyle: 'italic',
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                  }}
                                >
                                  "{currentSession.initialConcept}"
                                </Typography>
                              </Box>
                            )}
                            {h.feedback && (
                              <Box sx={{ mb: 2 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  gutterBottom
                                  sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                                >
                                  Feedback:
                                </Typography>
                                <Typography 
                                  variant="body2"
                                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                  {h.feedback}
                                </Typography>
                              </Box>
                            )}
                            {h.changes && h.changes.length > 0 && (
                              <Box>
                                <Typography 
                                  variant="subtitle2" 
                                  gutterBottom
                                  sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                                >
                                  {isMobile ? 'Changes:' : 'Changes Made:'}
                                </Typography>
                                {h.changes.map((c, i) => (
                                  <Typography 
                                    key={i} 
                                    variant="body2"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                  >
                                    • {c}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </TabPanel>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
                    <PersonIcon sx={{ 
                      fontSize: { xs: 64, sm: 80 }, 
                      color: 'text.secondary', 
                      mb: 2 
                    }} />
                    <Typography 
                      variant="h6" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                    >
                      {isMobile ? 'Create a character' : 'Create a character to get started'}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </TabPanel>

            <TabPanel value={mainTabValue} index={1}>
              {sessions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
                  <TimelineIcon sx={{ 
                    fontSize: { xs: 48, sm: 60 }, 
                    color: 'text.secondary', 
                    mb: 2 
                  }} />
                  <Typography 
                    variant="h6" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    No character sessions yet
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mt: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {isMobile ? 'Create your first character' : 'Create your first character to begin'}
                  </Typography>
                </Box>
              ) : (
                <List>
                  {sessions.map((session) => (
                    <React.Fragment key={session.id}>
                      <ListItem
                        sx={{ 
                          borderRadius: 1,
                          mb: 1,
                          p: { xs: 1.5, sm: 2 },
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <Avatar sx={{ 
                          bgcolor: 'primary.main', 
                          mr: 2,
                          width: { xs: 32, sm: 40 },
                          height: { xs: 32, sm: 40 }
                        }}>
                          <PersonIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                        </Avatar>
                        <ListItemText
                          primary={
                            <Typography sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                              {session.currentCharacter?.name || 'Unnamed Character'}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography 
                                component="span" 
                                variant="body2" 
                                color="text.primary"
                                sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                              >
                                {session.currentCharacter?.genre || 'Fantasy'} • {session.history?.length || 0} {isMobile ? 'iter.' : 'iterations'}
                              </Typography>
                              <br />
                              <Typography 
                                component="span" 
                                variant="caption"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              >
                                {isMobile ? new Date(session.createdAt).toLocaleDateString(undefined, {month: 'short', day: 'numeric'}) : `Created: ${new Date(session.createdAt).toLocaleString()}`}
                              </Typography>
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            onClick={() => handleLoadSession(session)}
                            title="Load Session"
                            sx={{ 
                              mr: 1,
                              minWidth: 44,
                              minHeight: 44
                            }}
                          >
                            <PlayIcon />
                          </IconButton>
                          <IconButton 
                            onClick={() => handleDeleteSession(session.id)}
                            title="Delete Session"
                            sx={{ 
                              minWidth: 44,
                              minHeight: 44
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </TabPanel>
          </Box>
        </Paper>

      </Box>
    </Container>
  );
};